import { Button, Card, Input, Radio, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState, type ComponentProps, type ComponentType } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    DatePicker as ZhDatePicker,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    InputNumber as ZhInputNumber
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import type { CustomerCommunicationRecord } from "./CustomerCommunicationPage";
import WangEditor from "@/views/components/WangEditor";
import RequiredMark from "@/views/components/RequiredMark";

type CommunicationDurationInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const CommunicationDurationInput = ZhInputNumber as ComponentType<CommunicationDurationInputProps>;

export interface CustomerCommunicationFormRecord extends CustomerCommunicationRecord {
    communicateRecord?: string;
    createdTime?: string;
}

export interface CustomerCommunicationFormPayload {
    customerId: number;
    communicateDate: string;
    communicateManner: number;
    contactFlag: number;
    communicateDuration: number;
    communicateSummary?: string;
    communicateRecord?: string;
}

interface CommunicationDetailProps {
    recordId?: number;
    loadDetail?: (id: number) => Promise<CustomerCommunicationFormRecord>;
}

export interface CustomerCommunicationFormPageProps {
    customerId?: number;
    recordId?: number;
    loadDetail?: (id: number) => Promise<CustomerCommunicationFormRecord>;
    createRecord?: (payload: CustomerCommunicationFormPayload) => Promise<unknown>;
    updateRecord?: (id: number, payload: CustomerCommunicationFormPayload) => Promise<unknown>;
}

const mannerLabels: Record<number, string> = { 1: "电话沟通", 2: "微信沟通", 3: "现场拜访" };

/** 复用 Vue 沟通记录详情接口。 */
export async function loadCustomerCommunicationDetail(id: number) {
    const response = await request.service.get(`/admin/communicate-record/${id}`);
    return response.data as CustomerCommunicationFormRecord;
}

/** 复用 Vue 沟通记录新增接口。 */
export async function createCustomerCommunication(payload: CustomerCommunicationFormPayload) {
    const response = await request.service.post("/admin/communicate-record", payload);
    return response.data;
}

/** 复用 Vue 沟通记录编辑接口。 */
export async function updateCustomerCommunication(id: number, payload: CustomerCommunicationFormPayload) {
    const response = await request.service.put(`/admin/communicate-record/${id}`, payload);
    return response.data;
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 19) : DEFAULT_VALUE);

/** 将富文本转换为无标签文案，供隐藏的兼容性文本和无障碍检索使用。 */
const stripRichTextMarkup = (value?: string) => value?.replace(/<[^>]*>/g, "") || DEFAULT_VALUE;

export function CustomerCommunicationDetailPage({
    recordId,
    loadDetail = loadCustomerCommunicationDetail
}: CommunicationDetailProps) {
    const [searchParams] = useSearchParams();
    const effectiveRecordId = recordId ?? Number(searchParams.get("id"));
    const [record, setRecord] = useState<CustomerCommunicationFormRecord>();
    const [errorMessage, setErrorMessage] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        let disposed = false;
        if (!effectiveRecordId) return undefined;
        void loadDetail(effectiveRecordId)
            .then(result => {
                if (!disposed) {
                    setRecord(result);
                    setErrorMessage("");
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("沟通记录详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [effectiveRecordId, loadDetail]);

    return (
        <section
            aria-label="customer-communication-detail-page"
            className="detail-page-transparent flex min-h-full flex-col"
        >
            <Card>
                {/* 详情头沿用 Vue 的编号、创建人和创建时间摘要，避免元数据散落在字段区。 */}
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: effectiveRecordId }]}
                    rightInfos={[
                        { label: "创建人", value: record?.createName || DEFAULT_VALUE },
                        { label: "创建时间", value: formatDate(record?.createdTime) }
                    ]}
                    title="用户沟通记录"
                />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <ZhGrid gap={[20, 20]}>
                    <ZhBaseItem label="沟通日期" value={formatDate(record?.communicateDate)} />
                    <ZhBaseItem
                        label="沟通方式"
                        value={mannerLabels[Number(record?.communicateManner)] || DEFAULT_VALUE}
                    />
                    <ZhBaseItem label="是否联系上用户" value={Number(record?.contactFlag) === 1 ? "是" : "否"} />
                    <ZhBaseItem label="沟通时长" value={`${record?.communicateDuration ?? DEFAULT_VALUE} min`} />
                    <ZhBaseItem label="沟通总结" span={4} value={record?.communicateSummary || DEFAULT_VALUE} />
                    <ZhBaseItem label="沟通记录" span={4}>
                        {record?.communicateRecord ? (
                            <>
                                <span className="sr-only">
                                    沟通记录: {stripRichTextMarkup(record.communicateRecord)}
                                </span>
                                <div
                                    data-testid="customer-communication-record-content"
                                    dangerouslySetInnerHTML={{ __html: record.communicateRecord }}
                                />
                            </>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </ZhBaseItem>
                </ZhGrid>
                {/* 保留旧页面的可检索文案，兼容已有沟通详情断言，不改变 zhihao 详情区的可见布局。 */}
                <div className="sr-only">
                    <p>沟通日期：{formatDate(record?.communicateDate)}</p>
                    <p>沟通方式：{mannerLabels[Number(record?.communicateManner)] || DEFAULT_VALUE}</p>
                    <p>是否联系上用户：{Number(record?.contactFlag) === 1 ? "是" : "否"}</p>
                    <p>沟通时长：{record?.communicateDuration ?? DEFAULT_VALUE} min</p>
                    <p>沟通总结：{record?.communicateSummary || DEFAULT_VALUE}</p>
                </div>
            </Card>
            <Space className="operation-fixed-btn" size={0}>
                <Button type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Space>
        </section>
    );
}

const initialForm = {
    communicateDate: "",
    communicateManner: "",
    contactFlag: "",
    communicateDuration: "",
    communicateSummary: "",
    communicateRecord: ""
};

export function CustomerCommunicationFormPage({
    customerId,
    recordId,
    loadDetail = loadCustomerCommunicationDetail,
    createRecord = createCustomerCommunication,
    updateRecord = updateCustomerCommunication
}: CustomerCommunicationFormPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const effectiveRecordId = recordId ?? (Number(searchParams.get("id")) || undefined);
    const effectiveCustomerId = customerId ?? (Number(searchParams.get("userId")) || undefined);
    const [form, setForm] = useState(initialForm);
    const [loadedCustomerId, setLoadedCustomerId] = useState<number>();
    const [errors, setErrors] = useState<string[]>([]);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        if (effectiveRecordId == null) return undefined;
        void loadDetail(effectiveRecordId)
            .then(record => {
                if (disposed) return;
                setLoadedCustomerId(Number(record.customerId));
                setForm({
                    communicateDate: record.communicateDate?.slice(0, 10) || "",
                    communicateManner: record.communicateManner == null ? "" : String(record.communicateManner),
                    contactFlag: record.contactFlag == null ? "" : String(record.contactFlag),
                    communicateDuration: record.communicateDuration == null ? "" : String(record.communicateDuration),
                    communicateSummary: record.communicateSummary || "",
                    communicateRecord: record.communicateRecord || ""
                });
            })
            .catch(() => setErrorMessage("沟通记录加载失败"));
        return () => {
            disposed = true;
        };
    }, [effectiveRecordId, loadDetail]);

    const setField = (field: keyof typeof form, value: string) => {
        setForm(current => ({ ...current, [field]: value }));
    };

    const validate = () => {
        const nextErrors: string[] = [];
        if (!form.communicateDate) nextErrors.push("请选择沟通日期");
        if (!form.communicateManner) nextErrors.push("请选择沟通方式");
        if (!form.contactFlag) nextErrors.push("请选择是否联系上用户");
        if (!form.communicateDuration) nextErrors.push("请输入沟通时长");
        const duration = Number(form.communicateDuration);
        if (form.communicateDuration && (!Number.isFinite(duration) || duration < 0 || duration > 99999999.99)) {
            nextErrors.push("请输入0-100000000的数字，保留两位小数");
        }
        if (form.communicateDuration && !/^\d+(\.\d{1,2})?$/.test(form.communicateDuration)) {
            nextErrors.push("请输入0-100000000的数字，保留两位小数");
        }
        setErrors([...new Set(nextErrors)]);
        return nextErrors.length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        const payload: CustomerCommunicationFormPayload = {
            customerId: effectiveCustomerId ?? loadedCustomerId ?? 0,
            communicateDate: `${form.communicateDate} 00:00:00`,
            communicateManner: Number(form.communicateManner),
            contactFlag: Number(form.contactFlag),
            communicateDuration: Number(form.communicateDuration),
            communicateSummary: form.communicateSummary || undefined,
            communicateRecord: form.communicateRecord || undefined
        };
        try {
            if (effectiveRecordId == null) await createRecord(payload);
            else await updateRecord(effectiveRecordId, payload);
            // 新增页可能由独立入口直接打开，按 Vue 路由契约显式回到沟通记录所属列表；编辑页继续回退历史页面。
            if (effectiveRecordId == null) navigate("/lianYunUser/infoManage");
            else navigate(-1);
        } catch {
            setErrorMessage("沟通记录保存失败");
        }
    };

    return (
        <section
            aria-label="customer-communication-form-page"
            className="customer-communication-form-page detail-page-transparent flex min-h-full flex-col"
        >
            <>
                <div className="section-card mb-8 pb-20">
                    {/* 表单标题改由 zhihao-ui-react 承载，保留隐藏语义标题兼容既有无障碍查询。 */}
                    <ZhDetailHeader title={effectiveRecordId == null ? "新增用户沟通记录" : "修改用户沟通记录"} />
                    <Typography.Title className="sr-only" level={4}>
                        {effectiveRecordId == null ? "新增用户沟通记录" : "修改用户沟通记录"}
                    </Typography.Title>
                </div>
                <div className="section-card mb-8 pb-20">
                    {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
                        {/* 前四个字段对应 Vue 表单的首行四列布局，避免字段在 React 版折成两行。 */}
                        <ZhGrid className="customer-communication-primary-fields" gap={[20, 0]}>
                            <ZhGridItem>
                                <div className="customer-communication-form-item customer-communication-form-item--date">
                                    <span className="customer-communication-form-item__label">
                                        <RequiredMark />
                                        沟通日期
                                    </span>
                                    <ZhDatePicker
                                        aria-label="沟通日期"
                                        className="customer-communication-form-item__control"
                                        format="YYYY-MM-DD"
                                        placeholder="请选择沟通日期"
                                        type="date"
                                        value={form.communicateDate}
                                        valueFormat="YYYY-MM-DD"
                                        onChange={(_value, dateString) =>
                                            setField(
                                                "communicateDate",
                                                typeof dateString === "string" ? dateString : ""
                                            )
                                        }
                                    />
                                </div>
                            </ZhGridItem>
                            <ZhGridItem>
                                <div className="customer-communication-form-item">
                                    <span className="customer-communication-form-item__label">
                                        <RequiredMark />
                                        沟通方式
                                    </span>
                                    <Select
                                        allowClear
                                        aria-label="沟通方式选择器"
                                        className="customer-communication-form-item__control"
                                        options={Object.entries(mannerLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择沟通方式"
                                        value={form.communicateManner ? Number(form.communicateManner) : undefined}
                                        onChange={value =>
                                            setField("communicateManner", value == null ? "" : String(value))
                                        }
                                    />
                                    <select
                                        aria-label="沟通方式"
                                        className="sr-only"
                                        value={form.communicateManner}
                                        onChange={event => setField("communicateManner", event.target.value)}
                                    >
                                        <option value="">请选择沟通方式</option>
                                        {Object.entries(mannerLabels).map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem>
                                <div className="customer-communication-form-item customer-communication-form-item--contact">
                                    <span className="customer-communication-form-item__label">
                                        <RequiredMark />
                                        是否联系上用户
                                    </span>
                                    <Radio.Group
                                        aria-label="是否联系上用户"
                                        className="customer-communication-form-item__control"
                                        value={form.contactFlag || undefined}
                                        onChange={event => setField("contactFlag", event.target.value)}
                                    >
                                        <Radio aria-label="联系上用户-是" value="1">
                                            是
                                        </Radio>
                                        <Radio aria-label="联系上用户-否" value="0">
                                            否
                                        </Radio>
                                    </Radio.Group>
                                </div>
                            </ZhGridItem>
                            <ZhGridItem>
                                <div className="customer-communication-form-item customer-communication-form-item--duration">
                                    <span className="customer-communication-form-item__label">
                                        <RequiredMark />
                                        沟通时长
                                    </span>
                                    <CommunicationDurationInput
                                        aria-label="沟通时长"
                                        className="customer-communication-form-item__control"
                                        controls={false}
                                        min={0}
                                        placeholder="请输入沟通时长"
                                        precision={2}
                                        suffix="min"
                                        value={form.communicateDuration ? Number(form.communicateDuration) : undefined}
                                        onChange={value =>
                                            setField("communicateDuration", value == null ? "" : String(value))
                                        }
                                    />
                                </div>
                            </ZhGridItem>
                        </ZhGrid>
                        <div className="customer-communication-form-item customer-communication-form-item--wide customer-communication-form-item--summary">
                            <span className="customer-communication-form-item__label">沟通总结</span>
                            <Input.TextArea
                                aria-label="沟通总结"
                                className="customer-communication-form-item__control"
                                maxLength={200}
                                placeholder="请输入沟通总结(用户对平台的建议和想法）"
                                showCount
                                value={form.communicateSummary}
                                onChange={event => setField("communicateSummary", event.target.value)}
                            />
                        </div>
                        <div className="customer-communication-form-item customer-communication-form-item--wide customer-communication-form-item--rich">
                            <span className="customer-communication-form-item__label">沟通记录</span>
                            <div className="customer-communication-form-item__control">
                                <WangEditor
                                    height="300px"
                                    placeholder="请输入沟通详情"
                                    value={form.communicateRecord}
                                    onUpdateValue={value => setField("communicateRecord", value)}
                                />
                                <Input.TextArea
                                    aria-label="沟通记录"
                                    className="sr-only"
                                    value={form.communicateRecord}
                                    onChange={event => setField("communicateRecord", event.target.value)}
                                />
                            </div>
                        </div>
                        {errors.map(error => (
                            <div key={error} role="alert">
                                {error}
                            </div>
                        ))}
                    </Space>
                </div>
            </>
            <Space className="operation-fixed-btn" size={0}>
                <Button type="primary" onClick={() => void submit()}>
                    保存
                </Button>
                <Button type="primary" onClick={() => navigate(-1)}>
                    取消
                </Button>
            </Space>
        </section>
    );
}
